/**
 * NOVA CHARTER — procedural audio engine.
 *
 * Everything you hear is synthesised at runtime: there are no binary assets in the
 * repository. The engine owns one lazily-created `AudioContext`, a small mix bus
 * (SFX / music faders → compressor → output trim) and two convolution reverbs whose
 * impulse responses are generated from filtered, exponentially-decaying noise.
 *
 * Design rules that keep the mix coherent:
 *
 * - **One key.** Every pitched sound is derived from D minor (`ROOT_HZ`), so stingers,
 *   UI blips and the generative score in `music.ts` never fight each other.
 * - **Per-seat pitch identity.** Seats map to scale degrees 0/3/5/7 of a D minor
 *   pentatonic (root, minor third, fourth, fifth). Any two seats acting at the same
 *   moment produce a consonant interval instead of a beating cluster.
 * - **Bounded polyphony.** At most `MAX_VOICES` voices sound at once; past that, a new
 *   request only plays if it is louder than the quietest voice already sounding (which
 *   is then stolen with a fast fade rather than clicked off).
 * - **Never throws.** If Web Audio is missing or construction fails, `ready` stays
 *   false and every public method becomes a silent no-op.
 *
 * Golden rule: this module may read `../game/types` and nothing else from the app.
 */

import type { ActionKind, EventId, GameNotice, Phase } from '../game/types';
import { MusicEngine } from './music';

export type { GameNotice, Phase } from '../game/types';

/* ---------------------------------------------------------------- public API */

export interface AudioSettings {
  master: number;
  sfx: number;
  music: number;
  muted: boolean;
}

export type SfxId =
  | 'cursorMove' | 'select' | 'cancel' | 'illegal' | 'menuOpen' | 'menuClose'
  | 'survey' | 'claim' | 'upgrade' | 'terraform' | 'deployFleet' | 'jump'
  | 'research' | 'trade' | 'pass' | 'production' | 'relic' | 'megastructure'
  | 'phaseChange' | 'roundStart' | 'eventGood' | 'eventBad' | 'eventMixed'
  | 'bidTick' | 'bidLock' | 'bidReveal' | 'contestWin' | 'contestLose'
  | 'scoreTick' | 'victory' | 'defeat' | 'join' | 'countdown';

/** Options accepted by {@link AudioEngine.play}. */
export interface SfxOptions {
  /** Seat 0..3. Shifts the sound onto that player's scale degree. */
  seat?: number;
  /** Extra detune in cents, applied on top of the seat offset. */
  detune?: number;
  /** Linear gain multiplier, 1 = the sound's designed level. */
  gain?: number;
}

/* ---------------------------------------------------------------- constants */

/** Hard polyphony ceiling. Above this, quiet requests are dropped. */
const MAX_VOICES = 24;

/** `cursorMove` fires on every stick tick; ignore retriggers inside this window. */
const CURSOR_GUARD = 0.025;

/** D4. The entire game is in D minor. */
const ROOT_HZ = 293.6648;

/** Seat → semitones: root, minor 3rd, 4th, 5th of the D minor pentatonic. */
const SEAT_SEMIS: readonly number[] = [0, 3, 5, 7];

/** Long shared reverb (space, stingers, music) and a short plate (UI). */
const HALL_SECONDS = 2.2;
const PLATE_SECONDS = 0.45;

/** Designed peak level per sound, before the sfx fader. Keeps the mix balanced. */
const SFX_LEVEL: Readonly<Record<SfxId, number>> = {
  cursorMove: 0.05,
  select: 0.20,
  cancel: 0.17,
  illegal: 0.26,
  menuOpen: 0.18,
  menuClose: 0.16,
  survey: 0.28,
  claim: 0.40,
  upgrade: 0.44,
  terraform: 0.34,
  deployFleet: 0.34,
  jump: 0.34,
  research: 0.30,
  trade: 0.24,
  pass: 0.20,
  production: 0.28,
  relic: 0.40,
  megastructure: 0.55,
  phaseChange: 0.28,
  roundStart: 0.38,
  eventGood: 0.38,
  eventBad: 0.40,
  eventMixed: 0.38,
  bidTick: 0.15,
  bidLock: 0.28,
  bidReveal: 0.48,
  contestWin: 0.40,
  contestLose: 0.40,
  scoreTick: 0.18,
  victory: 0.58,
  defeat: 0.52,
  join: 0.28,
  countdown: 0.26,
};

/** Command-phase actions → their sound. `move` reads as a fleet jump. */
const ACTION_SFX: Readonly<Record<ActionKind, SfxId>> = {
  survey: 'survey',
  move: 'jump',
  deployFleet: 'deployFleet',
  claim: 'claim',
  upgrade: 'upgrade',
  terraform: 'terraform',
  research: 'research',
  trade: 'trade',
  megastructure: 'megastructure',
  pass: 'pass',
};

/**
 * Emotional colour per event card. The engine's `eventCard` notice only carries an id,
 * and the audio layer must not depend on the event table, so the tone lives here.
 */
const EVENT_TONE: Readonly<Record<EventId, 'good' | 'bad' | 'mixed'>> = {
  solarFlare: 'bad',
  oreRush: 'good',
  bumperHarvest: 'good',
  ionStorm: 'bad',
  dataLeak: 'mixed',
  refugeeConvoy: 'mixed',
  quantumBloom: 'good',
  pirateRaid: 'bad',
  coreSurge: 'mixed',
  graviticShear: 'bad',
  openMarkets: 'good',
  cometHarvest: 'good',
};

/* ------------------------------------------------------------------ helpers */

/** Equal-tempered frequency: `semis` semitones above D4, shifted by `octave` octaves. */
function hz(semis: number, octave = 0): number {
  return ROOT_HZ * Math.pow(2, semis / 12 + octave);
}

function clamp01(v: number): number {
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0;
}

/** Symmetric random spread of ±`c` cents, used to stop repeated hits phasing. */
function jitter(c: number): number {
  return (Math.random() * 2 - 1) * c;
}

/* -------------------------------------------------------------- graph + voice */

/** The whole live audio graph. Recreated by `unlock()`, dropped by `dispose()`. */
interface Graph {
  ctx: AudioContext;
  /** Post-compressor output trim — this is what mute and master volume drive. */
  out: GainNode;
  /** Pre-compressor summing point for every dry and wet path. */
  bus: GainNode;
  sfx: GainNode;
  music: GainNode;
  /** Per-fader reverb sends, so wet level tracks its own slider. */
  sfxHall: GainNode;
  sfxPlate: GainNode;
  musicHall: GainNode;
  /** Soft-clip bus for gritty, distorted layers. */
  gritIn: GainNode;
  noise: AudioBuffer;
}

interface Voice {
  out: GainNode;
  /** Designed peak level, used to decide what to steal. */
  level: number;
  timer: number;
  done: boolean;
}

/** Reverb/grit send amounts for a single voice, 0..1. */
interface Sends {
  hall?: number;
  plate?: number;
  grit?: number;
}

interface ToneCfg {
  type?: OscillatorType;
  freq: number;
  /** Pitch target for an exponential glide (doppler bends, thud envelopes). */
  to?: number;
  glide?: number;
  detune?: number;
  peak?: number;
  attack?: number;
  hold?: number;
  decay?: number;
  /** Optional lowpass, with an optional sweep to `lpTo`. */
  lp?: number;
  lpTo?: number;
  q?: number;
}

interface NoiseCfg {
  peak?: number;
  attack?: number;
  hold?: number;
  decay?: number;
  type?: BiquadFilterType;
  freq: number;
  /** Filter sweep target — the backbone of whooshes and settle tails. */
  to?: number;
  q?: number;
  /** Resample the shared noise buffer for a coarser or finer grain. */
  rate?: number;
}

interface BellCfg {
  /** Modulator:carrier ratio. Non-integer ratios read as metallic. */
  ratio?: number;
  /** FM index — modulation depth as a multiple of the carrier frequency. */
  index?: number;
  peak?: number;
  attack?: number;
  decay?: number;
  detune?: number;
}

/** A scheduled sound request: when to fire, at what pitch offset and level. */
interface Shot {
  t: number;
  cents: number;
  gain: number;
}

/* ------------------------------------------------------------------- engine */

export class AudioEngine {
  /** Live-mutable mixer state. The properties are accessors that clamp and apply. */
  settings: AudioSettings;

  private g: Graph | null = null;
  private music: MusicEngine | null = null;
  private failed = false;
  private disposed = false;

  private readonly store = { master: 0.8, sfx: 0.9, music: 0.5, muted: false };
  private readonly active: Voice[] = [];
  private readonly timers = new Set<number>();

  private lastCursor = -1;
  private tension = 0;
  private phase: Phase = 'setup';
  private round = 1;
  private totalRounds = 8;
  private wantMusic = false;

  constructor() {
    // No AudioContext here: browsers refuse to start one outside a user gesture, and
    // this class is instantiated at module load for the shared singleton below.
    this.settings = this.makeSettings();
  }

  /* ------------------------------------------------------------- lifecycle */

  /**
   * Create (or resume) the audio graph. Safe and cheap to call on every input event —
   * that is exactly how it is meant to be used, since only a gesture-handler call is
   * allowed to start audio.
   */
  unlock(): void {
    if (this.disposed || this.failed) return;
    let g = this.g;
    if (!g) {
      g = this.build();
      if (!g) {
        this.failed = true;
        return;
      }
      this.g = g;
      this.music = new MusicEngine({
        ctx: g.ctx,
        out: g.music,
        send: g.musicHall,
        noise: g.noise,
      });
      this.music.setPhase(this.phase, this.round, this.totalRounds);
      this.music.setTension(this.tension);
      this.applyGains();
    }
    // Autoplay policy: a context created before the gesture lands starts suspended.
    if (g.ctx.state !== 'running') void g.ctx.resume().catch(() => undefined);
    if (this.wantMusic) this.music?.start();
  }

  get ready(): boolean {
    return !!this.g && this.g.ctx.state === 'running';
  }

  dispose(): void {
    this.disposed = true;
    this.wantMusic = false;
    for (const id of this.timers) clearTimeout(id);
    this.timers.clear();
    this.music?.dispose();
    this.music = null;
    const g = this.g;
    this.g = null;
    for (const v of this.active.splice(0)) {
      v.done = true;
      try {
        v.out.disconnect();
      } catch {
        /* already detached */
      }
    }
    if (!g) return;
    try {
      g.out.disconnect();
      void g.ctx.close().catch(() => undefined);
    } catch {
      /* nothing sensible to do — the context is going away regardless */
    }
  }

  /* ---------------------------------------------------------------- mixer */

  setMaster(v: number): void {
    this.settings.master = v;
  }

  setSfx(v: number): void {
    this.settings.sfx = v;
  }

  setMusic(v: number): void {
    this.settings.music = v;
  }

  toggleMute(): boolean {
    this.settings.muted = !this.settings.muted;
    return this.settings.muted;
  }

  /** Build the accessor-backed settings object so external writes still clamp+apply. */
  private makeSettings(): AudioSettings {
    const s = {} as AudioSettings;
    const num = (key: 'master' | 'sfx' | 'music'): PropertyDescriptor => ({
      enumerable: true,
      configurable: false,
      get: () => this.store[key],
      set: (v: number) => {
        this.store[key] = clamp01(v);
        this.applyGains();
      },
    });
    Object.defineProperties(s, {
      master: num('master'),
      sfx: num('sfx'),
      music: num('music'),
      muted: {
        enumerable: true,
        configurable: false,
        get: () => this.store.muted,
        set: (v: boolean) => {
          this.store.muted = !!v;
          this.applyGains();
        },
      },
    });
    return s;
  }

  /** Push the fader values into the graph with a short glide (no zipper noise). */
  private applyGains(): void {
    const g = this.g;
    if (!g) return;
    const t = g.ctx.currentTime;
    const set = (p: AudioParam, v: number): void => {
      p.cancelScheduledValues(t);
      p.setTargetAtTime(v, t, 0.02);
    };
    const { master, sfx, music, muted } = this.store;
    set(g.out.gain, muted ? 0 : master);
    set(g.sfx.gain, sfx);
    // The sends mirror their fader so a muted sfx bus cannot leak through the reverb.
    set(g.sfxHall.gain, sfx);
    set(g.sfxPlate.gain, sfx);
    set(g.music.gain, music);
    set(g.musicHall.gain, music);
  }

  /* -------------------------------------------------------------- notices */

  /**
   * Route an engine notice straight to a sound. Presentational only: dropping a notice
   * never changes the game, so every branch here is allowed to do nothing.
   */
  notice(n: GameNotice): void {
    if (!this.ready) return;
    switch (n.type) {
      case 'action':
        this.play(ACTION_SFX[n.kind], { seat: n.player });
        return;
      case 'illegal':
        this.play('illegal', { seat: n.player });
        return;
      case 'phase':
        this.round = n.round;
        this.setMusicPhase(n.phase, n.round, this.totalRounds);
        if (n.phase === 'setup') return;
        this.play(n.phase === 'drift' ? 'roundStart' : 'phaseChange');
        return;
      case 'production':
        this.play('production', { seat: n.player });
        return;
      case 'bidLocked':
        this.play('bidLock', { seat: n.player });
        return;
      case 'bidReveal': {
        this.play('bidReveal');
        // Land the verdict after the reveal stab, on the audio clock rather than a timer.
        const t = this.now() + 1.05;
        if (n.outcome.winner !== null) {
          this.at('contestWin', t, { seat: n.outcome.winner });
        }
        for (let i = 0; i < n.outcome.losers.length; i++) {
          this.at('contestLose', t + 0.08 * (i + 1), { seat: n.outcome.losers[i], gain: 0.8 });
        }
        return;
      }
      case 'claimed':
        this.play(n.development === 'colony' ? 'upgrade' : 'claim', { seat: n.player });
        return;
      case 'relic':
        this.play('relic', { seat: n.player });
        return;
      case 'tech':
        this.play('research', { seat: n.player });
        return;
      case 'megastructure':
        this.play('megastructure', { seat: n.player });
        return;
      case 'eventCard': {
        const tone = EVENT_TONE[n.eventId] ?? 'mixed';
        this.play(tone === 'good' ? 'eventGood' : tone === 'bad' ? 'eventBad' : 'eventMixed');
        return;
      }
      case 'gameOver':
        this.setMusicPhase('gameOver', this.round, this.totalRounds);
        this.play(n.winner !== null ? 'victory' : 'defeat', { seat: n.winner ?? 0 });
        return;
    }
  }

  /* ----------------------------------------------------------------- sfx */

  play(id: SfxId, opts?: SfxOptions): void {
    if (!this.ready) return;
    this.at(id, this.now(), opts);
  }

  /** Schedule a sound at an absolute context time. */
  private at(id: SfxId, t: number, opts?: SfxOptions): void {
    const g = this.g;
    if (!g) return;
    const seat = opts?.seat;
    const cents =
      (seat !== undefined && seat >= 0 ? SEAT_SEMIS[seat % SEAT_SEMIS.length] * 100 : 0) +
      (opts?.detune ?? 0);
    const gain = SFX_LEVEL[id] * (opts?.gain ?? 1);
    if (!(gain > 0.0005)) return;
    try {
      this.render(id, { t, cents, gain });
    } catch {
      // A single malformed request must never take the audio layer down.
    }
  }

  private now(): number {
    const g = this.g;
    return g ? g.ctx.currentTime + 0.015 : 0;
  }

  private render(id: SfxId, s: Shot): void {
    switch (id) {
      case 'cursorMove': return this.sCursor(s);
      case 'select': return this.sBlip(s, 1);
      case 'cancel': return this.sBlip(s, -1);
      case 'join': return this.sJoin(s);
      case 'illegal': return this.sIllegal(s);
      case 'menuOpen': return this.sMenu(s, true);
      case 'menuClose': return this.sMenu(s, false);
      case 'survey': return this.sSurvey(s);
      case 'claim': return this.sBuild(s, 0);
      case 'upgrade': return this.sBuild(s, 1);
      case 'megastructure': return this.sBuild(s, 2);
      case 'terraform': return this.sTerraform(s);
      case 'deployFleet': return this.sDeploy(s);
      case 'jump': return this.sJump(s);
      case 'research': return this.sResearch(s);
      case 'trade': return this.sTrade(s);
      case 'pass': return this.sPass(s);
      case 'production': return this.sProduction(s);
      case 'relic': return this.sRelic(s);
      case 'phaseChange': return this.sPhase(s, false);
      case 'roundStart': return this.sPhase(s, true);
      case 'eventGood': return this.sSting(s, 'good');
      case 'eventBad': return this.sSting(s, 'bad');
      case 'eventMixed': return this.sSting(s, 'mixed');
      case 'bidTick': return this.sBidTick(s);
      case 'bidLock': return this.sBidLock(s);
      case 'bidReveal': return this.sBidReveal(s);
      case 'contestWin': return this.sContest(s, true);
      case 'contestLose': return this.sContest(s, false);
      case 'scoreTick': return this.sScoreTick(s);
      case 'countdown': return this.sCountdown(s);
      case 'victory': return this.sVictory(s);
      case 'defeat': return this.sDefeat(s);
    }
  }

  /* ------------------------------------------------------------ sfx voices */

  /**
   * A dry, low-gain filtered click. Fires dozens of times a second while a stick is
   * held, so it is deliberately tiny, slightly randomised (to avoid a machine-gun
   * comb effect) and rate-limited.
   */
  private sCursor(s: Shot): void {
    const g = this.g;
    if (!g) return;
    if (s.t - this.lastCursor < CURSOR_GUARD) return;
    this.lastCursor = s.t;
    const out = this.voice(s.gain, s.t, 0.06, { plate: 0.08 });
    if (!out) return;
    this.nz(out, s.t, {
      freq: 1900 * Math.pow(2, jitter(140) / 1200),
      to: 900,
      q: 1.6,
      attack: 0.001,
      decay: 0.028,
    });
    this.tone(out, s.t, {
      type: 'triangle',
      freq: hz(12 + s.cents / 100),
      peak: 0.35,
      attack: 0.001,
      decay: 0.025,
    });
  }

  /** Confirm (`dir` 1, rising) / cancel (`dir` -1, falling) UI blip. */
  private sBlip(s: Shot, dir: 1 | -1): void {
    const out = this.voice(s.gain, s.t, 0.3, { plate: 0.3 });
    if (!out) return;
    const base = dir > 0 ? hz(7) : hz(3);
    const target = dir > 0 ? hz(14) : hz(-2);
    this.tone(out, s.t, {
      type: 'triangle',
      freq: base,
      to: target,
      glide: 0.07,
      detune: s.cents,
      peak: 0.7,
      attack: 0.003,
      decay: 0.16,
    });
    // A quiet octave partial keeps it audible over the music without raising level.
    this.tone(out, s.t + 0.012, {
      type: 'sine',
      freq: base * 2,
      to: target * 2,
      glide: 0.06,
      detune: s.cents,
      peak: 0.22,
      attack: 0.002,
      decay: 0.1,
    });
    this.nz(out, s.t, { freq: 3200, to: 2000, q: 2, peak: 0.18, attack: 0.001, decay: 0.03 });
  }

  /** "Press A to join" — warm rising two-note in the seat's own colour. */
  private sJoin(s: Shot): void {
    const out = this.voice(s.gain, s.t, 0.9, { hall: 0.3, plate: 0.2 });
    if (!out) return;
    for (let i = 0; i < 2; i++) {
      this.tone(out, s.t + i * 0.11, {
        type: 'triangle',
        freq: hz(i === 0 ? 0 : 7),
        detune: s.cents,
        peak: 0.6,
        attack: 0.006,
        decay: 0.42,
        lp: 2600,
      });
    }
    this.bell(out, s.t + 0.11, hz(12, 1), { ratio: 3.01, index: 1.2, peak: 0.2, decay: 0.5 });
  }

  /** Illegal action: a dull, clipped double thud. Discouraging, never harsh. */
  private sIllegal(s: Shot): void {
    const out = this.voice(s.gain, s.t, 0.5, { grit: 0.5, plate: 0.15 });
    if (!out) return;
    for (let i = 0; i < 2; i++) {
      const t = s.t + i * 0.085;
      this.tone(out, t, {
        type: 'sawtooth',
        freq: hz(1, -2),
        to: hz(0, -2),
        glide: 0.09,
        detune: s.cents * 0.25,
        peak: 0.8 - i * 0.25,
        attack: 0.002,
        decay: 0.11,
        lp: 700,
        lpTo: 260,
        q: 3,
      });
      this.nz(out, t, { freq: 320, to: 160, q: 0.9, peak: 0.3, attack: 0.001, decay: 0.06 });
    }
  }

  /** Radial menu open/close: a short noise swish plus a soft fifth pad. */
  private sMenu(s: Shot, open: boolean): void {
    const out = this.voice(s.gain, s.t, 0.5, { plate: 0.35, hall: 0.1 });
    if (!out) return;
    this.nz(out, s.t, {
      freq: open ? 500 : 4200,
      to: open ? 4200 : 500,
      q: 0.8,
      peak: 0.5,
      attack: open ? 0.02 : 0.004,
      decay: 0.2,
    });
    for (const semi of [0, 7]) {
      this.tone(out, s.t, {
        type: 'sine',
        freq: hz(semi, open ? 0 : -1),
        to: hz(semi, open ? 1 : -1) * (open ? 1 : 0.5),
        glide: 0.18,
        peak: 0.35,
        attack: open ? 0.03 : 0.005,
        decay: 0.22,
      });
    }
  }

  /** Sonar ping into a long tail — the sound of looking into the dark. */
  private sSurvey(s: Shot): void {
    const out = this.voice(s.gain, s.t, 1.6, { hall: 0.6 });
    if (!out) return;
    this.tone(out, s.t, {
      type: 'sine',
      freq: hz(19),
      detune: s.cents,
      peak: 0.7,
      attack: 0.004,
      decay: 0.5,
    });
    this.tone(out, s.t + 0.16, {
      type: 'sine',
      freq: hz(24),
      detune: s.cents,
      peak: 0.4,
      attack: 0.004,
      decay: 0.7,
    });
    // Airy sparkle: a narrow band of noise that rises as the ping "returns".
    this.nz(out, s.t + 0.04, {
      freq: 2600,
      to: 6800,
      q: 5,
      peak: 0.2,
      attack: 0.05,
      decay: 0.45,
    });
  }

  /**
   * Construction family — claim (0), upgrade (1), megastructure (2). All three share a
   * three-layer anatomy so the empire always sounds like the same empire:
   *   1. a low sine thud with a downward pitch envelope (weight),
   *   2. an FM bell at a metallic ratio (the machinery),
   *   3. a filtered noise "settle" tail (dust, plates cooling).
   */
  private sBuild(s: Shot, tier: 0 | 1 | 2): void {
    const out = this.voice(s.gain, s.t, tier === 2 ? 3.4 : 1.6, {
      hall: 0.3 + tier * 0.15,
      plate: 0.12,
    });
    if (!out) return;
    const oct = tier === 2 ? -3 : -2;

    this.tone(out, s.t, {
      type: 'sine',
      freq: hz(12, oct),
      to: hz(0, oct),
      glide: tier === 2 ? 0.28 : 0.14,
      peak: 0.9,
      attack: 0.004,
      decay: tier === 2 ? 0.85 : 0.42,
    });

    // Bell: a fifth above the seat's degree for upgrades, unison for claims.
    const bellSemis = s.cents / 100 + (tier === 0 ? 0 : 7) + (tier === 2 ? 12 : 0);
    this.bell(out, s.t + 0.01, hz(bellSemis, tier === 2 ? 1 : 0), {
      ratio: tier === 1 ? 3.46 : 2.76,
      index: 1.4 + tier * 0.5,
      peak: 0.45,
      attack: 0.002,
      decay: 0.7 + tier * 0.5,
    });
    if (tier > 0) {
      this.bell(out, s.t + 0.09, hz(bellSemis + 12, 0), {
        ratio: 2.01,
        index: 0.9,
        peak: 0.22,
        decay: 0.9,
      });
    }
    if (tier === 2) {
      // Megastructure: a wide, slow fifths pad swelling under the impact.
      for (const semi of [0, 7, 12, 19]) {
        this.tone(out, s.t + 0.02, {
          type: 'sawtooth',
          freq: hz(semi, -1),
          detune: jitter(9),
          peak: 0.2,
          attack: 0.5,
          hold: 0.25,
          decay: 1.6,
          lp: 400,
          lpTo: 1500,
          q: 1.2,
        });
      }
    }

    this.nz(out, s.t + 0.02, {
      freq: tier === 2 ? 3000 : 2200,
      to: 200,
      q: 0.7,
      peak: 0.3,
      attack: 0.01,
      decay: tier === 2 ? 1.4 : 0.55,
    });
  }

  /** Terraform: a resonant filter bloom over a minor triad. Growth, not impact. */
  private sTerraform(s: Shot): void {
    const out = this.voice(s.gain, s.t, 2.0, { hall: 0.5 });
    if (!out) return;
    this.nz(out, s.t, {
      freq: 200,
      to: 3200,
      q: 7,
      peak: 0.4,
      attack: 0.25,
      hold: 0.1,
      decay: 0.7,
    });
    [0, 3, 7, 10].forEach((semi, i) => {
      this.tone(out, s.t + i * 0.06, {
        type: 'triangle',
        freq: hz(semi + s.cents / 100),
        peak: 0.32,
        attack: 0.08,
        decay: 0.8,
        lp: 2400,
      });
    });
  }

  /** Fleet deployment: a mechanical thump and a short saw stab dropping in pitch. */
  private sDeploy(s: Shot): void {
    const out = this.voice(s.gain, s.t, 0.9, { hall: 0.22, plate: 0.15 });
    if (!out) return;
    this.nz(out, s.t, { freq: 900, to: 130, q: 1.1, peak: 0.55, attack: 0.002, decay: 0.16 });
    this.tone(out, s.t, {
      type: 'sawtooth',
      freq: hz(7, -1),
      to: hz(0, -2),
      glide: 0.2,
      detune: s.cents * 0.5,
      peak: 0.5,
      attack: 0.004,
      decay: 0.3,
      lp: 1800,
      lpTo: 500,
      q: 2.5,
    });
    this.tone(out, s.t + 0.03, {
      type: 'square',
      freq: hz(12 + s.cents / 100),
      peak: 0.12,
      attack: 0.002,
      decay: 0.1,
      lp: 3000,
    });
  }

  /** Jump: noise sweeps up into the gate, then a doppler pitch bend falls away. */
  private sJump(s: Shot): void {
    const out = this.voice(s.gain, s.t, 1.4, { hall: 0.5 });
    if (!out) return;
    this.nz(out, s.t, {
      freq: 220,
      to: 6500,
      q: 2.5,
      peak: 0.5,
      attack: 0.22,
      decay: 0.12,
    });
    // The bend starts as the sweep peaks — the "passing by" cue.
    const t2 = s.t + 0.22;
    this.tone(out, t2, {
      type: 'triangle',
      freq: hz(24 + s.cents / 100),
      to: hz(-5, -1),
      glide: 0.5,
      peak: 0.55,
      attack: 0.006,
      decay: 0.55,
      lp: 4200,
      lpTo: 700,
      q: 1.5,
    });
    this.nz(out, t2, { freq: 5000, to: 400, q: 0.9, peak: 0.25, attack: 0.005, decay: 0.5 });
  }

  /** Research: two ascending FM bells — a thought completing. */
  private sResearch(s: Shot): void {
    const out = this.voice(s.gain, s.t, 1.8, { hall: 0.45, plate: 0.15 });
    if (!out) return;
    [0, 7].forEach((semi, i) => {
      this.bell(out, s.t + i * 0.1, hz(semi + 12 + s.cents / 100), {
        ratio: 3.01,
        index: 1.1,
        peak: 0.5 - i * 0.1,
        decay: 0.85,
      });
    });
    this.nz(out, s.t, { freq: 5200, to: 8000, q: 6, peak: 0.12, attack: 0.02, decay: 0.35 });
  }

  /** Trade: down then up — a swap gesture, with a small mechanical tick. */
  private sTrade(s: Shot): void {
    const out = this.voice(s.gain, s.t, 0.7, { plate: 0.3 });
    if (!out) return;
    this.tone(out, s.t, {
      type: 'triangle',
      freq: hz(7),
      to: hz(3),
      glide: 0.06,
      detune: s.cents,
      peak: 0.55,
      attack: 0.003,
      decay: 0.14,
    });
    this.tone(out, s.t + 0.1, {
      type: 'triangle',
      freq: hz(3),
      to: hz(10),
      glide: 0.06,
      detune: s.cents,
      peak: 0.55,
      attack: 0.003,
      decay: 0.2,
    });
    this.nz(out, s.t + 0.09, { freq: 2600, q: 3, peak: 0.16, attack: 0.001, decay: 0.03 });
  }

  /** Pass: a low, resigned exhale. */
  private sPass(s: Shot): void {
    const out = this.voice(s.gain, s.t, 0.9, { hall: 0.25 });
    if (!out) return;
    this.nz(out, s.t, {
      type: 'lowpass',
      freq: 1200,
      to: 220,
      q: 0.8,
      peak: 0.45,
      attack: 0.05,
      decay: 0.4,
    });
    this.tone(out, s.t, {
      type: 'sine',
      freq: hz(0, -1),
      to: hz(-2, -1),
      glide: 0.35,
      detune: s.cents * 0.5,
      peak: 0.35,
      attack: 0.04,
      decay: 0.4,
    });
  }

  /** Production: a rising pentatonic pluck run — the sound of income. */
  private sProduction(s: Shot): void {
    const out = this.voice(s.gain, s.t, 1.4, { hall: 0.4, plate: 0.1 });
    if (!out) return;
    [0, 5, 10].forEach((semi, i) => {
      this.tone(out, s.t + i * 0.075, {
        type: 'triangle',
        freq: hz(semi + 12 + s.cents / 100),
        peak: 0.45 - i * 0.05,
        attack: 0.003,
        decay: 0.3 + i * 0.1,
        lp: 3600,
      });
    });
  }

  /** Relic: an ancient, slowly-blooming inharmonic cluster. */
  private sRelic(s: Shot): void {
    const out = this.voice(s.gain, s.t, 3.2, { hall: 0.8 });
    if (!out) return;
    [0, 3, 7, 14, 22].forEach((semi, i) => {
      this.bell(out, s.t + i * 0.06, hz(semi + s.cents / 100, 1), {
        ratio: 1.41 + i * 0.37,
        index: 0.8 + i * 0.15,
        peak: 0.3 - i * 0.03,
        attack: 0.02 + i * 0.03,
        decay: 1.6 + i * 0.3,
      });
    });
    this.nz(out, s.t, {
      freq: 400,
      to: 7000,
      q: 4,
      peak: 0.2,
      attack: 0.6,
      decay: 1.2,
    });
  }

  /** Phase change (short interval) / round start (a low tom plus rising fifths). */
  private sPhase(s: Shot, round: boolean): void {
    const out = this.voice(s.gain, s.t, round ? 2.2 : 1.0, { hall: round ? 0.55 : 0.3 });
    if (!out) return;
    if (round) {
      this.tone(out, s.t, {
        type: 'sine',
        freq: hz(7, -2),
        to: hz(0, -3),
        glide: 0.2,
        peak: 0.85,
        attack: 0.004,
        decay: 0.5,
      });
      [0, 7, 12].forEach((semi, i) => {
        this.tone(out, s.t + 0.05 + i * 0.08, {
          type: 'sawtooth',
          freq: hz(semi, -1),
          detune: jitter(7),
          peak: 0.24,
          attack: 0.12,
          hold: 0.1,
          decay: 0.9,
          lp: 700,
          lpTo: 2200,
          q: 1.4,
        });
      });
      this.nz(out, s.t, { freq: 6000, to: 900, q: 1, peak: 0.22, attack: 0.006, decay: 0.5 });
      return;
    }
    [0, 5].forEach((semi, i) => {
      this.tone(out, s.t + i * 0.09, {
        type: 'triangle',
        freq: hz(semi + 7),
        peak: 0.5,
        attack: 0.006,
        decay: 0.32,
        lp: 2800,
      });
    });
  }

  /** Three-note event stings. Distinct contours so tone reads without looking up. */
  private sSting(s: Shot, tone: 'good' | 'bad' | 'mixed'): void {
    const out = this.voice(s.gain, s.t, 2.0, { hall: 0.5, grit: tone === 'bad' ? 0.35 : 0 });
    if (!out) return;
    const shapes: Record<'good' | 'bad' | 'mixed', number[]> = {
      good: [7, 12, 16], // rising, opens to a major third — hopeful
      bad: [8, 5, 1], // falling onto a b9 — sour
      mixed: [0, 6, 7], // tritone that resolves to the fifth — unresolved, then fine
    };
    const semis = shapes[tone];
    const type: OscillatorType = tone === 'bad' ? 'sawtooth' : 'triangle';
    semis.forEach((semi, i) => {
      const t = s.t + i * 0.13;
      this.tone(out, t, {
        type,
        freq: hz(semi, tone === 'bad' ? -1 : 0),
        detune: tone === 'bad' ? jitter(16) : 0,
        peak: 0.55,
        attack: tone === 'good' ? 0.004 : 0.012,
        decay: i === 2 ? 0.9 : 0.26,
        lp: tone === 'bad' ? 1500 : 4000,
        q: 1.6,
      });
      if (tone !== 'bad') {
        this.bell(out, t, hz(semi + 12, 0), { ratio: 2.01, index: 0.7, peak: 0.16, decay: 0.5 });
      }
    });
    if (tone === 'bad') {
      this.nz(out, s.t, { freq: 900, to: 120, q: 1.2, peak: 0.3, attack: 0.02, decay: 0.7 });
    }
  }

  /** Bid countdown tick. Pitch and bite both tighten with tension. */
  private sBidTick(s: Shot): void {
    const out = this.voice(s.gain * (0.75 + this.tension * 0.5), s.t, 0.2, { plate: 0.2 });
    if (!out) return;
    const t = this.tension;
    this.nz(out, s.t, {
      freq: 1400 + t * 2600,
      q: 3 + t * 6,
      peak: 0.5,
      attack: 0.001,
      decay: 0.03 - t * 0.012,
    });
    this.tone(out, s.t, {
      type: 'square',
      freq: hz(12 + t * 12 + s.cents / 100),
      peak: 0.22 + t * 0.2,
      attack: 0.001,
      decay: 0.05,
      lp: 4000,
    });
  }

  /** Bid locked in: a firm mechanical double-click with a low thunk under it. */
  private sBidLock(s: Shot): void {
    const out = this.voice(s.gain, s.t, 0.5, { plate: 0.25, hall: 0.12 });
    if (!out) return;
    for (let i = 0; i < 2; i++) {
      this.nz(out, s.t + i * 0.035, {
        freq: 2400 - i * 700,
        q: 4,
        peak: 0.5 - i * 0.15,
        attack: 0.001,
        decay: 0.02,
      });
    }
    this.tone(out, s.t + 0.03, {
      type: 'sine',
      freq: hz(0, -2),
      to: hz(-5, -2),
      glide: 0.1,
      detune: s.cents * 0.5,
      peak: 0.6,
      attack: 0.003,
      decay: 0.22,
    });
  }

  /**
   * Reveal: a reverse swell (linear gain ramp on a rising noise band) leads into an
   * unfiltered saw stab, so the ear is pulled to the exact frame the bids flip over.
   */
  private sBidReveal(s: Shot): void {
    const out = this.voice(s.gain, s.t, 2.6, { hall: 0.6 });
    if (!out) return;
    const hit = s.t + 0.5;
    // Reverse swell: slow linear attack that ends abruptly at the impact.
    this.nz(out, s.t, {
      freq: 300,
      to: 5200,
      q: 1.4,
      peak: 0.34,
      attack: 0.48,
      decay: 0.02,
    });
    this.tone(out, s.t, {
      type: 'sawtooth',
      freq: hz(0, -1),
      to: hz(7, -1),
      glide: 0.5,
      peak: 0.2,
      attack: 0.48,
      decay: 0.03,
      lp: 300,
      lpTo: 2200,
      q: 2,
    });
    // The stab itself: deliberately unfiltered, so it cuts through everything.
    for (const semi of [0, 7, 12]) {
      this.tone(out, hit, {
        type: 'sawtooth',
        freq: hz(semi, -1),
        detune: jitter(11),
        peak: 0.4,
        attack: 0.002,
        decay: 0.5,
      });
    }
    this.tone(out, hit, {
      type: 'sine',
      freq: hz(12, -2),
      to: hz(0, -3),
      glide: 0.2,
      peak: 0.9,
      attack: 0.003,
      decay: 0.6,
    });
    this.nz(out, hit, { freq: 7000, to: 800, q: 0.8, peak: 0.3, attack: 0.002, decay: 0.7 });
  }

  /** Contest verdict: a rising fifth for the winner, a gritty falling sixth for a loss. */
  private sContest(s: Shot, win: boolean): void {
    const out = this.voice(s.gain, s.t, 1.8, { hall: 0.45, grit: win ? 0 : 0.45 });
    if (!out) return;
    const semis = win ? [0, 7, 12] : [8, 3, -1];
    semis.forEach((semi, i) => {
      this.tone(out, s.t + i * 0.09, {
        type: win ? 'triangle' : 'sawtooth',
        freq: hz(semi + s.cents / 100, win ? 0 : -1),
        detune: win ? 0 : jitter(20),
        peak: win ? 0.55 : 0.5,
        attack: 0.004,
        decay: i === 2 ? 0.8 : 0.22,
        lp: win ? 5000 : 1400,
        lpTo: win ? undefined : 500,
        q: 1.5,
      });
    });
    if (win) {
      this.bell(out, s.t + 0.18, hz(19 + s.cents / 100), {
        ratio: 3.01,
        index: 1.3,
        peak: 0.3,
        decay: 1.0,
      });
    } else {
      this.nz(out, s.t, { freq: 700, to: 100, q: 1, peak: 0.35, attack: 0.02, decay: 0.8 });
    }
  }

  /** Scoreboard counter tick. Tiny, bright, seat-coloured. */
  private sScoreTick(s: Shot): void {
    const out = this.voice(s.gain, s.t, 0.4, { plate: 0.3 });
    if (!out) return;
    this.tone(out, s.t, {
      type: 'triangle',
      freq: hz(24 + s.cents / 100),
      peak: 0.5,
      attack: 0.002,
      decay: 0.12,
      lp: 6000,
    });
    this.nz(out, s.t, { freq: 4800, q: 5, peak: 0.14, attack: 0.001, decay: 0.02 });
  }

  /** Clock countdown: a dry woodblock rap. Urgent but not shrill. */
  private sCountdown(s: Shot): void {
    const out = this.voice(s.gain, s.t, 0.3, { plate: 0.18 });
    if (!out) return;
    this.tone(out, s.t, {
      type: 'square',
      freq: hz(5 + s.cents / 100, 1),
      to: hz(0, 1),
      glide: 0.03,
      peak: 0.4,
      attack: 0.001,
      decay: 0.07,
      lp: 3400,
      q: 1.2,
    });
    this.nz(out, s.t, { freq: 1700, q: 6, peak: 0.4, attack: 0.001, decay: 0.025 });
  }

  /** Victory: a D major arpeggio riding a swelling pad. */
  private sVictory(s: Shot): void {
    const out = this.voice(s.gain, s.t, 4.0, { hall: 0.7 });
    if (!out) return;
    [0, 4, 7, 12, 16, 19].forEach((semi, i) => {
      this.bell(out, s.t + 0.09 + i * 0.085, hz(semi, 0), {
        ratio: 2.01,
        index: 0.9,
        peak: 0.34,
        attack: 0.003,
        decay: 1.1 + i * 0.12,
      });
      this.tone(out, s.t + 0.09 + i * 0.085, {
        type: 'triangle',
        freq: hz(semi, 0),
        peak: 0.26,
        attack: 0.004,
        decay: 0.5,
        lp: 4200,
      });
    });
    // Pad: root, fifth and octave, slow attack, filter opening as it blooms.
    for (const semi of [0, 7, 12]) {
      this.tone(out, s.t, {
        type: 'sawtooth',
        freq: hz(semi, -2),
        detune: jitter(8),
        peak: 0.22,
        attack: 0.8,
        hold: 0.5,
        decay: 2.0,
        lp: 350,
        lpTo: 2600,
        q: 1.3,
      });
    }
  }

  /** Defeat: detuned fifths falling by whole steps, with grit. */
  private sDefeat(s: Shot): void {
    const out = this.voice(s.gain, s.t, 3.6, { hall: 0.6, grit: 0.4 });
    if (!out) return;
    [0, -2, -4].forEach((root, i) => {
      const t = s.t + i * 0.34;
      for (const semi of [root, root + 7]) {
        this.tone(out, t, {
          type: 'sawtooth',
          freq: hz(semi, -1),
          detune: jitter(24),
          peak: 0.34,
          attack: 0.03,
          hold: 0.1,
          decay: 0.9 + i * 0.4,
          lp: 1600,
          lpTo: 420,
          q: 2,
        });
      }
      this.tone(out, t, {
        type: 'sine',
        freq: hz(root, -3),
        peak: 0.5,
        attack: 0.01,
        decay: 0.7,
      });
    });
    this.nz(out, s.t + 0.68, { freq: 800, to: 90, q: 1, peak: 0.28, attack: 0.15, decay: 1.4 });
  }

  /* ---------------------------------------------------------- DSP primitives */

  /**
   * Percussive AD(H) envelope: linear attack (click-free), exponential decay (natural).
   * Returns the total length in seconds so callers can size their voice budget.
   */
  private env(p: AudioParam, t: number, peak: number, attack: number, decay: number, hold = 0): number {
    const a = Math.max(0.0006, attack);
    const d = Math.max(0.008, decay);
    const h = Math.max(0, hold);
    const top = Math.max(0.0004, peak);
    p.setValueAtTime(0.0001, t);
    p.linearRampToValueAtTime(top, t + a);
    if (h > 0) p.setValueAtTime(top, t + a + h);
    // Exponential ramps cannot reach 0, so decay to near-silence then hard-zero.
    p.exponentialRampToValueAtTime(0.0001, t + a + h + d);
    p.setValueAtTime(0, t + a + h + d + 0.002);
    return a + h + d + 0.004;
  }

  /** Band-limited noise burst with an optional filter sweep. */
  private nz(out: GainNode, t: number, c: NoiseCfg): number {
    const g = this.g;
    if (!g) return 0;
    const src = g.ctx.createBufferSource();
    src.buffer = g.noise;
    src.loop = true;
    src.playbackRate.value = c.rate ?? 1;

    const bq = g.ctx.createBiquadFilter();
    bq.type = c.type ?? 'bandpass';
    bq.Q.value = c.q ?? 1;
    bq.frequency.setValueAtTime(Math.max(20, c.freq), t);

    const amp = g.ctx.createGain();
    const dur = this.env(amp.gain, t, c.peak ?? 1, c.attack ?? 0.003, c.decay ?? 0.1, c.hold ?? 0);
    if (c.to !== undefined) {
      bq.frequency.exponentialRampToValueAtTime(Math.max(20, c.to), t + dur);
    }

    src.connect(bq).connect(amp).connect(out);
    // Random read offset: repeated hits from one buffer would otherwise comb-filter.
    src.start(t, Math.random() * 1.2, dur + 0.02);
    return dur;
  }

  /** Single oscillator with pitch and amplitude envelopes, optional lowpass sweep. */
  private tone(out: GainNode, t: number, c: ToneCfg): number {
    const g = this.g;
    if (!g) return 0;
    const osc = g.ctx.createOscillator();
    osc.type = c.type ?? 'sine';
    osc.detune.value = c.detune ?? 0;
    osc.frequency.setValueAtTime(Math.max(20, c.freq), t);

    const amp = g.ctx.createGain();
    const dur = this.env(amp.gain, t, c.peak ?? 1, c.attack ?? 0.004, c.decay ?? 0.2, c.hold ?? 0);
    if (c.to !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(20, c.to), t + Math.min(dur, c.glide ?? dur));
    }

    let node: AudioNode = osc;
    if (c.lp !== undefined) {
      const lp = g.ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.Q.value = c.q ?? 0.7;
      lp.frequency.setValueAtTime(Math.max(40, c.lp), t);
      if (c.lpTo !== undefined) {
        lp.frequency.exponentialRampToValueAtTime(Math.max(40, c.lpTo), t + dur);
      }
      node.connect(lp);
      node = lp;
    }
    node.connect(amp).connect(out);
    osc.start(t);
    osc.stop(t + dur + 0.03);
    return dur;
  }

  /**
   * Two-operator FM bell. A single modulator at a non-integer ratio (2.76 ≈ tubular
   * bell, 3.01 ≈ glassy, 1.41 ≈ ancient/inharmonic) produces the dense inharmonic
   * partials that read as "metal" — impossible with a plain oscillator.
   */
  private bell(out: GainNode, t: number, freq: number, c: BellCfg): number {
    const g = this.g;
    if (!g) return 0;
    const f = Math.max(20, freq);
    const carrier = g.ctx.createOscillator();
    carrier.type = 'sine';
    carrier.frequency.value = f;
    carrier.detune.value = c.detune ?? 0;

    const mod = g.ctx.createOscillator();
    mod.type = 'sine';
    mod.frequency.value = f * (c.ratio ?? 2.76);

    const amp = g.ctx.createGain();
    const dur = this.env(amp.gain, t, c.peak ?? 0.5, c.attack ?? 0.002, c.decay ?? 0.8);

    // The modulation index decays faster than the amplitude: bright strike, pure tail.
    const idx = g.ctx.createGain();
    idx.gain.setValueAtTime(f * (c.index ?? 1.2), t);
    idx.gain.exponentialRampToValueAtTime(f * 0.01, t + dur * 0.45);

    mod.connect(idx).connect(carrier.frequency);
    carrier.connect(amp).connect(out);
    mod.start(t);
    carrier.start(t);
    mod.stop(t + dur + 0.03);
    carrier.stop(t + dur + 0.03);
    return dur;
  }

  /* ------------------------------------------------------- voice allocation */

  /**
   * Allocate a voice bus. Returns null when the polyphony budget is exhausted and the
   * request is not loud enough to be worth stealing for — the caller then does nothing.
   */
  private voice(level: number, t: number, dur: number, sends?: Sends): GainNode | null {
    const g = this.g;
    if (!g) return null;
    const now = g.ctx.currentTime;
    if (this.active.length >= MAX_VOICES) {
      let q = 0;
      for (let i = 1; i < this.active.length; i++) {
        if (this.active[i].level < this.active[q].level) q = i;
      }
      const quietest = this.active[q];
      if (quietest.level >= level) return null; // the new sound is the least important
      this.release(quietest, now, 0.01);
      this.active.splice(q, 1);
    }

    const out = g.ctx.createGain();
    out.gain.value = level;
    out.connect(g.sfx);
    if (sends) {
      this.send(out, sends.hall, g.sfxHall);
      this.send(out, sends.plate, g.sfxPlate);
      this.send(out, sends.grit, g.gritIn);
    }

    const v: Voice = { out, level, timer: 0, done: false };
    const lifetime = Math.max(0.05, t - now + dur + 0.25);
    v.timer = this.later(() => {
      const i = this.active.indexOf(v);
      if (i >= 0) this.active.splice(i, 1);
      this.release(v, this.g ? this.g.ctx.currentTime : 0, 0);
    }, lifetime * 1000);
    this.active.push(v);
    return out;
  }

  private send(from: GainNode, amount: number | undefined, to: GainNode): void {
    const g = this.g;
    if (!g || !amount || amount <= 0) return;
    const s = g.ctx.createGain();
    s.gain.value = amount;
    from.connect(s);
    s.connect(to);
  }

  /** Fade a voice out over `fade` seconds, then detach it from the graph. */
  private release(v: Voice, now: number, fade: number): void {
    if (v.done) return;
    v.done = true;
    clearTimeout(v.timer);
    this.timers.delete(v.timer);
    try {
      if (fade > 0) {
        v.out.gain.cancelScheduledValues(now);
        v.out.gain.setTargetAtTime(0, now, fade);
        this.later(() => v.out.disconnect(), fade * 4000 + 40);
      } else {
        v.out.disconnect();
      }
    } catch {
      /* already detached */
    }
  }

  private later(fn: () => void, ms: number): number {
    if (typeof window === 'undefined') return 0;
    const id = window.setTimeout(() => {
      this.timers.delete(id);
      try {
        fn();
      } catch {
        /* housekeeping must never surface an error */
      }
    }, ms);
    this.timers.add(id);
    return id;
  }

  /* ------------------------------------------------------------------ music */

  setMusicPhase(phase: Phase, round: number, totalRounds: number): void {
    this.phase = phase;
    this.round = round;
    this.totalRounds = Math.max(1, totalRounds);
    this.music?.setPhase(phase, this.round, this.totalRounds);
  }

  setTension(t: number): void {
    this.tension = clamp01(t);
    this.music?.setTension(this.tension);
  }

  startMusic(): void {
    if (this.disposed) return;
    this.wantMusic = true;
    this.music?.start();
  }

  stopMusic(): void {
    this.wantMusic = false;
    this.music?.stop();
  }

  /* ------------------------------------------------------------ graph build */

  /** Build the mix bus, both reverbs and the shared noise table. Never throws. */
  private build(): Graph | null {
    try {
      if (typeof window === 'undefined') return null;
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      const ctx = new Ctor({ latencyHint: 'interactive' });

      // Master chain: everything sums into `bus`, is glued by the compressor, then
      // trimmed by `out` so mute and master volume sit after the dynamics.
      const out = ctx.createGain();
      out.gain.value = 0;
      out.connect(ctx.destination);

      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -14;
      comp.knee.value = 12;
      comp.ratio.value = 3.5;
      comp.attack.value = 0.004;
      comp.release.value = 0.2;
      comp.connect(out);

      const bus = ctx.createGain();
      bus.gain.value = 1;
      bus.connect(comp);

      const sfx = ctx.createGain();
      sfx.connect(bus);
      const music = ctx.createGain();
      music.connect(bus);

      // Reverbs: a 2.2 s hall for the world, a short bright plate for UI.
      const hall = ctx.createConvolver();
      hall.normalize = false;
      hall.buffer = this.makeImpulse(ctx, HALL_SECONDS, {
        decay: 2.6,
        bright: 0.5,
        damp: 0.82,
        preDelay: 0.018,
        reflections: 7,
      });
      const hallRet = ctx.createGain();
      hallRet.gain.value = 0.9;
      hall.connect(hallRet).connect(bus);

      const plate = ctx.createConvolver();
      plate.normalize = false;
      plate.buffer = this.makeImpulse(ctx, PLATE_SECONDS, {
        decay: 3.4,
        bright: 0.85,
        damp: 0.45,
        preDelay: 0.004,
        reflections: 3,
      });
      const plateRet = ctx.createGain();
      plateRet.gain.value = 0.75;
      plate.connect(plateRet).connect(bus);

      const sfxHall = ctx.createGain();
      sfxHall.connect(hall);
      const musicHall = ctx.createGain();
      musicHall.connect(hall);
      const sfxPlate = ctx.createGain();
      sfxPlate.connect(plate);

      // Grit bus: shared soft-clipper for "wrong" and "losing" sounds. Post-shaper
      // lowpass tames the harsh upper harmonics the folding creates.
      const shaper = ctx.createWaveShaper();
      shaper.curve = this.makeSaturation(2.6);
      shaper.oversample = '2x';
      const gritTone = ctx.createBiquadFilter();
      gritTone.type = 'lowpass';
      gritTone.frequency.value = 2600;
      const gritIn = ctx.createGain();
      gritIn.gain.value = 1;
      gritIn.connect(shaper).connect(gritTone).connect(sfx);

      return {
        ctx,
        out,
        bus,
        sfx,
        music,
        sfxHall,
        sfxPlate,
        musicHall,
        gritIn,
        noise: this.noiseBuffer(ctx),
      };
    } catch {
      return null;
    }
  }

  /** Two seconds of stereo white noise, shared by every noise-based voice. */
  private noiseBuffer(ctx: AudioContext): AudioBuffer {
    const len = Math.floor(ctx.sampleRate * 2);
    const buf = ctx.createBuffer(2, len, ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    }
    return buf;
  }

  /**
   * Procedural impulse response: exponentially-decaying noise, one-pole lowpassed with
   * a coefficient that closes over the tail (air absorption), DC-blocked, sprinkled
   * with a few discrete early reflections, and energy-normalised so the wet level is
   * predictable regardless of length. The two channels use independent noise and a
   * slightly different pre-delay, which is what makes the tail feel wide.
   */
  private makeImpulse(
    ctx: AudioContext,
    seconds: number,
    o: { decay: number; bright: number; damp: number; preDelay: number; reflections: number },
  ): AudioBuffer {
    const rate = ctx.sampleRate;
    const len = Math.max(64, Math.floor(seconds * rate));
    const buf = ctx.createBuffer(2, len, rate);
    let energy = 0;

    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      const pre = Math.floor((o.preDelay + ch * 0.0035) * rate);
      const span = Math.max(1, len - pre);
      let lp = 0;
      let dc = 0;
      for (let i = pre; i < len; i++) {
        const p = (i - pre) / span;
        const a = Math.max(0.02, o.bright * (1 - o.damp * p));
        lp += a * (Math.random() * 2 - 1 - lp);
        dc += 0.0016 * (lp - dc);
        d[i] = (lp - dc) * Math.pow(1 - p, o.decay);
      }
      // Early reflections: sparse, alternating-sign taps in the first ~90 ms.
      for (let k = 0; k < o.reflections; k++) {
        const at = pre + Math.floor((0.006 + 0.012 * k + ch * 0.002) * rate);
        if (at < len) d[at] += (k % 2 ? -1 : 1) * (0.5 / (k + 1));
      }
      for (let i = 0; i < len; i++) energy += d[i] * d[i];
    }

    const norm = energy > 0 ? 1 / Math.sqrt(energy) : 1;
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      for (let i = 0; i < len; i++) d[i] *= norm;
    }
    return buf;
  }

  /** Normalised tanh transfer curve for the grit bus (a `Float32Array` lookup table). */
  private makeSaturation(drive: number) {
    const n = 1024;
    const curve = new Float32Array(n);
    const k = Math.max(0.1, drive);
    for (let i = 0; i < n; i++) {
      const x = (i / (n - 1)) * 2 - 1;
      curve[i] = Math.tanh(x * k) / Math.tanh(k);
    }
    return curve;
  }
}

/** Shared singleton. Constructing it is inert until `unlock()` runs on a gesture. */
export const audio = new AudioEngine();
